import React from 'react'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import useFrontEndConfig from '../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import { buildDashboardHref, isDashboardUrlConfigured, machineDashboardParams } from './planMetricsDashboardLink'

// The job panel's round close control (44px, a gloved fingertip). Its accessible name is the literal.
const CloseButton = ({ onClose }) => {
  const { t_ } = useSmartTranslation()
  return (
    <button type='button' className='mdd__close' onClick={onClose} aria-label={t_('Close')} title={t_('Close')}>
      ✕
    </button>
  )
}

/** While loading or after a failure: the eyebrow and the close control, nothing else to name yet. */
export const MachineDetailSimpleHead = ({ onClose }) => {
  const { t_ } = useSmartTranslation()
  return (
    <header className='mdd__head'>
      <div className='mdd__head-text'>
        <div className='mdd__eyebrow'>{t_('Machine detail')}</div>
      </div>
      <CloseButton onClose={onClose} />
    </header>
  )
}

/**
 * Story 175, CU5 (S87) -- who the machine is: "code description". The line of groups, shifts, calendar
 * hours and OEE loss under the name was removed at the PO's request (2026-09-29): the head only names it.
 * S90 -- with global filters applied, a notice names them as the Planner's selectors show them.
 * S106 -- a window shorter than 4 weeks is announced when Sun flags it (`window.truncated`).
 */
const MachineDetailHeader = ({ detail, planificationId, plannerFilters, filterLabels = [], onClose }) => {
  const { t_ } = useSmartTranslation()
  const [config] = useFrontEndConfig()
  const { device } = detail

  const title = [device.name, device.description].filter((part) => part !== null && part !== undefined && part !== '').join(' ')

  const dashboardUrl = config ? config.planMetricsMachineDashboardUrl : undefined

  return (
    <header className='mdd__head' data-testid='machine-detail-header'>
      <div className='mdd__head-text'>
        <div className='mdd__eyebrow'>{t_('Machine detail')}</div>
        <h2 className='mdd__title' data-testid='machine-detail-title'>{title}</h2>
        {filterLabels.length > 0
          ? (
            <div className='mdd__notice' data-testid='machine-detail-filter-notice'>
              {`${t_('Figures filtered by')} ${filterLabels.join(' · ')}`}
            </div>
            )
          : null}
        {detail.window.truncated
          ? <div className='mdd__notice mdd__notice--info' role='status'>{t_('The plan horizon is shorter than 4 weeks')}</div>
          : null}
      </div>
      <div className='mdd__head-actions'>
        {isDashboardUrlConfigured(dashboardUrl)
          ? (
            // Contract §5/DL-17 freezes rel="noopener" verbatim; noopener is what protects the opener.
            // eslint-disable-next-line react/jsx-no-target-blank
            <a
              className='mdd__link-button'
              href={buildDashboardHref(dashboardUrl, machineDashboardParams({ planificationId, plannerFilters, deviceId: device.deviceId }))}
              target='_blank'
              rel='noopener'
            >
              {t_('Dashboard')}
            </a>
            )
          : null}
        <CloseButton onClose={onClose} />
      </div>
    </header>
  )
}

export default MachineDetailHeader
